import { SmartTruncateEnum } from '@talos/kyoko';
import { FUNDING_RATE_METRIC } from '../HistoricalFundingRate/tokens';
import { FundingRateBlotterView, FundingRateGroupBy, FundingRateUpdateMode } from './FundingRateBlotter.types';

/** Funding rate metric — shared with the Historical Funding Rate chart. */
export { FUNDING_RATE_METRIC };

/** Open interest metric (reported futures OI, USD). */
export const OPEN_INTEREST_METRIC = 'open_interest_reported_future_usd';
export const OPEN_INTEREST_METRIC_LABEL = 'Open Interest';

/** Settlement cadence of the underlying metric — fixed, since this app only ever queries the 8h-period series. */
export const FUNDING_RATE_SETTLES_LABEL = 'Every 8h';

/** Both metrics fetched together in one snapshot request. */
export const FUNDING_RATE_BLOTTER_METRICS = [FUNDING_RATE_METRIC, OPEN_INTEREST_METRIC];

/**
 * Display increment for the funding rate percent (4 dp). Shared between the cell's
 * `InlineFormattedNumber` increment and `isFlatRate`'s threshold so the two can't drift apart —
 * both must agree on what counts as "displays as zero".
 */
export const FUNDING_RATE_DISPLAY_INCREMENT = '0.0001';

/**
 * Shared open-interest number formatting: K/M/B abbreviated, dimmed USD unit (e.g. `1.4B USD`).
 * Used by both the Open Interest column cell and the rate-cell hover tooltip.
 */
export const OPEN_INTEREST_NUMBER_PROPS = {
  currency: 'USD',
  smartTruncate: SmartTruncateEnum.Compact,
  smartTruncateThreshold: 1000,
} as const;

/** CoinMetrics data frequency per update mode. */
export const FUNDING_RATE_UPDATE_MODE_FREQUENCY: Record<FundingRateUpdateMode, '1h' | '1d'> = {
  [FundingRateUpdateMode.Hourly]: '1h',
  [FundingRateUpdateMode.Daily]: '1d',
};

/**
 * RTK Query polling interval per update mode (ms). Hourly refreshes every hour to pick up the
 * latest 1h data point; Daily re-fetches once a day (EOD data changes at most once per day).
 */
export const FUNDING_RATE_UPDATE_MODE_POLL_MS: Record<FundingRateUpdateMode, number> = {
  [FundingRateUpdateMode.Hourly]: 60 * 60 * 1000,
  [FundingRateUpdateMode.Daily]: 24 * 60 * 60 * 1000,
};

/**
 * Opacity range for the Matrix heat chips. Magnitude is normalized against the largest |rate| in
 * the current snapshot: the floor keeps small non-zero rates visible; the ceiling is kept muted
 * (well below a solid fill) so the sign-colored text stays readable even on the strongest cell —
 * matching the design, where the highest-magnitude chips are a soft tint, not full green/red.
 */
export const FUNDING_RATE_HEAT_MIN_ALPHA = 0.15;
export const FUNDING_RATE_HEAT_MAX_ALPHA = 0.4;
export const FUNDING_RATE_MATRIX_ROW_HEIGHT = 32;

export const FUNDING_RATE_BLOTTER_DEFAULT_VIEW: FundingRateBlotterView = FundingRateBlotterView.List;
export const FUNDING_RATE_BLOTTER_DEFAULT_UPDATE_MODE: FundingRateUpdateMode = FundingRateUpdateMode.Daily;
export const FUNDING_RATE_BLOTTER_DEFAULT_GROUP_BY: FundingRateGroupBy = FundingRateGroupBy.None;

/** Default asset basket for a freshly-added panel (before the user configures it). */
export const FUNDING_RATE_BLOTTER_DEFAULT_CM_ASSET_IDS = ['btc', 'eth', 'sol'];

export const FUNDING_RATE_BLOTTER_UPDATE_MODE_LABELS: Record<FundingRateUpdateMode, string> = {
  [FundingRateUpdateMode.Hourly]: 'Hourly',
  [FundingRateUpdateMode.Daily]: 'Daily',
};

export const FUNDING_RATE_BLOTTER_VIEW_LABELS: Record<FundingRateBlotterView, string> = {
  [FundingRateBlotterView.List]: 'List',
  [FundingRateBlotterView.Matrix]: 'Matrix',
};

export const FUNDING_RATE_GROUP_BY_LABELS: Record<FundingRateGroupBy, string> = {
  [FundingRateGroupBy.None]: 'None',
  [FundingRateGroupBy.Asset]: 'By Asset',
  [FundingRateGroupBy.Exchange]: 'By Exchange',
};
